import React, { useState, useRef } from 'react';
import { MessageSquare } from 'lucide-react';
import { ToolLayout } from '../../components/ToolLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { printPreview } from '../../lib/print';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';

const sections = [
  { key: 'situation', letter: 'S', name: 'Situation', color: '#0D9488' },
  { key: 'background', letter: 'B', name: 'Background', color: '#2563EB' },
  { key: 'assessment', letter: 'A', name: 'Assessment', color: '#D97706' },
  { key: 'recommendation', letter: 'R', name: 'Recommendation', color: '#7C3AED' },
];
const blank = () => ({ title: 'SBAR Communication', situation: 'Patient name, location, and reason for the communication\nCurrent chief complaint or concern', background: 'Relevant history and diagnosis\nCurrent medications, allergies, vitals', assessment: 'Your clinical assessment of the situation\nWhat you think the problem is', recommendation: 'What you need and by when\nSpecific request or order needed' });

export default function SBAR() {
  const { user } = useAuth();
  const { toast } = useToast();
  const previewRef = useRef(null);
  const [data, setData] = useState(blank());
  const [saving, setSaving] = useState(false);
  const up = (patch) => setData((d) => ({ ...d, ...patch }));
  const save = async () => { if (!user) { toast({ title: 'Sign in to save', variant: 'destructive' }); return; } setSaving(true); try { await api.post('/onepagers', { title: data.title, docType: 'sbar-card', layout: 'sbar', data, colorScheme: { primary: '#0D9488', background: '#F0FDFA' } }); toast({ title: 'Saved to library!' }); } catch { toast({ title: 'Save failed', variant: 'destructive' }); } finally { setSaving(false); } };
  return (
    <ToolLayout title="SBAR Cards" subtitle="Structured communication" icon={MessageSquare} onSave={save} saving={saving} onPrint={() => printPreview(previewRef.current)} onNew={() => setData(blank())}>
      <div className="grid lg:grid-cols-[380px_1fr] gap-6">
        <Card><CardContent className="p-4 space-y-3">
          <div><Label className="text-xs">Card Title</Label><Input value={data.title} onChange={(e) => up({ title: e.target.value })} /></div>
          {sections.map((s) => (<div key={s.key}><Label className="text-xs" style={{ color: s.color }}>{s.name} (one prompt per line)</Label><Textarea rows={3} value={data[s.key]} onChange={(e) => up({ [s.key]: e.target.value })} /></div>))}
        </CardContent></Card>
        <div className="border rounded-xl bg-gray-100 p-4">
          <div ref={previewRef} className="onepager-preview bg-white p-8 shadow-lg mx-auto" style={{ width: '8.5in', minHeight: '11in', fontFamily: 'Inter' }}>
            <h1 className="text-2xl font-bold text-center text-teal-700 mb-4">{data.title}</h1>
            <div className="space-y-3">{sections.map((s) => (
              <div key={s.key} className="rounded-lg overflow-hidden border" style={{ borderColor: s.color }}>
                <div className="flex items-center gap-2 px-3 py-2 text-white" style={{ backgroundColor: s.color }}><span className="text-2xl font-extrabold">{s.letter}</span><span className="font-bold text-lg">{s.name}</span></div>
                <div className="p-3">{data[s.key].split('\n').filter(Boolean).map((l, i) => <div key={i} className="text-sm mb-1 flex gap-2"><span style={{ color: s.color }}>•</span>{l}</div>)}</div>
              </div>
            ))}</div>
          </div>
        </div>
      </div>
    </ToolLayout>
  );
}
